import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { ButtonBase } from '../private/components/button/ButtonBase';
import createTypography from '../utils/createTypography';

/**
 * A Floating Action Button, `FAB`, is positioned near the bottom of the screen
 * in order to make it easy for mobile device users to interact with.
 *
 * Use the `children` prop to provide the desired icon from the
 * [icon library](https://brand.homedepot.com/site/knapsack-nucleus/latest/pattern/icons?subPageId=DESIGN).
 *
 * Usage:
 *
 * ```jsx
 * import { Fab } from '@one-thd/sui-atomic-components';
 * ```
 */
const Fab = React.forwardRef((props, ref) => {

  const {
    children,
    component = 'button',
    disabled = false,
    label,
    ...other
  } = props;

  const classes = classNames('sui-h-11 sui-w-11 sui-rounded-full sui-shadow-md', {
    'sui-bg-inverse sui-text-inverse hover:sui-bg-brand hover:sui-text-inverse focus-visible:sui-bg-focus focus-visible:sui-text-primary': !disabled,
    'sui-btn-primary-inactive sui-text-inactive': disabled,
  });

  const fabLabel = createTypography({
    variant: 'body-xs',
    weight: 'bold',
    height: 'normal',
    color: 'none'
  });

  return (
    <ButtonBase
      className={classes}
      component={component}
      disabled={disabled}
      ref={ref}
      {...other}
    >
      <div className="sui-flex sui-flex-col sui-items-center [&>:not(style)+:not(style)]:sui--mt-2px">
        {children}
        {label && (
          <span className={fabLabel.classes}>
            {label}
          </span>
        )}
      </div>
    </ButtonBase>
  );
});

Fab.displayName = 'Fab';

Fab.propTypes = {
  /**
   * The content of the component.
   */
  children: PropTypes.node,
  /**
   * @ignore
   */
  className: PropTypes.string,
  /**
   * The component used for the root node.
   * Either a string to use a HTML element or a component.
   */
  component: PropTypes.elementType,
  /**
   * If `true`, the component is disabled.
   * @default false
   */
  disabled: PropTypes.bool,
  /**
   * The URL to link to when the button is clicked.
   * If defined, an `a` element will be used as the root node.
   */
  href: PropTypes.string,
  /**
   * Text to be displayed inside the button.
   */
  label: PropTypes.string,
};

Fab.defaultProps = {};

export { Fab };
